import { Show, For, createSignal, createMemo, onMount, type Component, type JSX } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { PanelBadge } from "@/shared/panel-primitives";
import { DataTable, type Column } from "@/shared/data-table";
import { usePanel } from "@/shared/use-panel";
import { useJobEvents } from "@/shared/use-job-events";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";
import { highlightToHtml } from "./lib/highlight";
import "./sql.css";

// ── Types ──────────────────────────────────────────────────────────────────────

interface SqlTarget {
  id: string;
  label: string;
  kind: string;
  host: string;
  port: number;
  database: string;
  user: string;
  allowDestructive: boolean;
  driver?: "auto" | "native" | "cli" | string;
  supportsNative?: boolean;
  supportsCli?: boolean;
}

interface TargetsListResult {
  count: number;
  rows: SqlTarget[];
}

interface QueryResult {
  columns: string[];
  rows: Record<string, string | null>[];
  rowCount: number;
  provider?: string;
  durationMs?: number;
  truncated?: boolean;
  stdoutBytes?: number;
  stderrBytes?: number;
  stdoutTruncated?: boolean;
  stderrTruncated?: boolean;
  timedOut?: boolean;
  queuedMs?: number;
  maxConcurrent?: number;
  fallback?: boolean;
  fallbackReason?: string | null;
}

interface ExecAsyncResult {
  jobId: string;
  async: boolean;
  status: string;
}

interface HistoryEntry {
  query: string;
  targetId: string;
  mode: "query" | "exec";
  ts: number;
}

type SqlRow = Record<string, string | null>;

const HISTORY_KEY = "remotews.sql.history.v1";
const HISTORY_MAX = 10;
const SQL_TOOL_TIMEOUT_MS = 6 * 60 * 60 * 1000;

// ── localStorage helpers ───────────────────────────────────────────────────────

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is HistoryEntry =>
        !!e &&
        typeof e === "object" &&
        typeof (e as HistoryEntry).query === "string" &&
        typeof (e as HistoryEntry).targetId === "string",
    );
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
  } catch {
    // quota or disabled storage — ignore
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Main panel ─────────────────────────────────────────────────────────────────

const SqlPanel: Component = () => {
  const panel = usePanel();

  const [targets, setTargets] = createSignal<SqlTarget[]>([]);
  const [targetsLoading, setTargetsLoading] = createSignal(false);
  const [targetsErr, setTargetsErr] = createSignal<string>("");
  const [targetId, setTargetId] = createSignal<string>("");

  const [query, setQuery] = createSignal<string>("");
  const [running, setRunning] = createSignal(false);
  const [sqlError, setSqlError] = createSignal<string>("");

  const [queryResult, setQueryResult] = createSignal<QueryResult | null>(null);
  const [execResult, setExecResult] = createSignal<{
    rowCount: number;
    provider?: string;
    durationMs?: number;
    columns?: string[];
    rows?: SqlRow[];
    truncated?: boolean;
    stdoutBytes?: number;
    stderrBytes?: number;
    stdoutTruncated?: boolean;
    stderrTruncated?: boolean;
    timedOut?: boolean;
    queuedMs?: number;
    maxConcurrent?: number;
    fallback?: boolean;
    fallbackReason?: string | null;
  } | null>(null);
  const [lastMode, setLastMode] = createSignal<"query" | "exec" | null>(null);

  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);
  const [history, setHistory] = createSignal<HistoryEntry[]>(loadHistory());

  // Calque <pre> coloré, dont le scroll est synchronisé avec le textarea.
  let highlightLayer: HTMLPreElement | undefined;

  // HTML coloré et échappé reflétant le contenu de l'éditeur.
  // Espace final : garde le calque aligné quand la requête se termine par \n.
  const highlightedQuery = createMemo<string>(() => highlightToHtml(query() + "\n", "sql"));

  const jobState = useJobEvents(() => activeJobId());

  const currentTarget = createMemo<SqlTarget | null>(() => {
    const id = targetId();
    if (!id) return null;
    return targets().find((t) => t.id === id) ?? null;
  });

  // ── Targets ──────────────────────────────────────────────────────────────────

  async function refreshTargets(): Promise<void> {
    setTargetsLoading(true);
    setTargetsErr("");
    try {
      const r = await panel.tools.call<TargetsListResult>("sql_targets_list", {});
      const rows = r?.rows ?? [];
      setTargets(rows);
      if (rows.length > 0 && !rows.find((t) => t.id === targetId())) {
        setTargetId(rows[0].id);
      } else if (rows.length === 0) {
        setTargetId("");
      }
    } catch (e: unknown) {
      setTargetsErr(e instanceof Error ? e.message : String(e));
      setTargets([]);
    } finally {
      setTargetsLoading(false);
    }
  }

  onMount(() => {
    if (panel.enabled()) {
      void refreshTargets();
    }
  });

  // ── History ──────────────────────────────────────────────────────────────────

  function pushHistory(entry: HistoryEntry): void {
    setHistory((prev) => {
      const filtered = prev.filter((e) => !(e.query === entry.query && e.targetId === entry.targetId));
      const next = [entry, ...filtered].slice(0, HISTORY_MAX);
      saveHistory(next);
      return next;
    });
  }

  function loadFromHistory(entry: HistoryEntry): void {
    setQuery(entry.query);
    if (targets().find((t) => t.id === entry.targetId)) {
      setTargetId(entry.targetId);
    }
  }

  function clearHistory(): void {
    setHistory([]);
    saveHistory([]);
  }

  // ── Run Query (SELECT) ───────────────────────────────────────────────────────

  async function runQuery(): Promise<void> {
    const t = currentTarget();
    const q = query().trim();
    if (!t) {
      setSqlError("Aucune cible SQL sélectionnée.");
      return;
    }
    if (!q) {
      setSqlError("Requête vide.");
      return;
    }
    setRunning(true);
    setSqlError("");
    setQueryResult(null);
    setExecResult(null);
    setLastMode("query");
    panel.setActionStatus({ text: "Exécution SELECT…", level: "info" });
    try {
      const r = await panel.tools.call<QueryResult>(
        "sql_query",
        { targetId: t.id, query: q, timeoutMs: SQL_TOOL_TIMEOUT_MS },
        { timeoutMs: SQL_TOOL_TIMEOUT_MS },
      );
      setQueryResult(r);
      pushHistory({ query: q, targetId: t.id, mode: "query", ts: Date.now() });
      panel.setActionStatus({
        text: `${r.rowCount ?? 0} ligne(s)${r.durationMs != null ? ` — ${r.durationMs} ms` : ""}${
          r.truncated ? " (tronqué)" : ""
        }`,
        level: "info",
      });
    } catch (e: unknown) {
      setSqlError(e instanceof Error ? e.message : String(e));
      panel.setActionStatus({ text: "Erreur SQL", level: "danger" });
    } finally {
      setRunning(false);
    }
  }

  // ── Run Exec (destructif) ────────────────────────────────────────────────────

  async function runExec(): Promise<void> {
    const t = currentTarget();
    const q = query().trim();
    if (!t) {
      setSqlError("Aucune cible SQL sélectionnée.");
      return;
    }
    if (!q) {
      setSqlError("Requête vide.");
      return;
    }
    if (!t.allowDestructive) {
      setSqlError(`La cible '${t.id}' n'autorise pas les opérations destructives (allowDestructive=false).`);
      return;
    }

    const preview = q.length > 200 ? q.slice(0, 200) + "…" : q;
    const ok = await panel.confirm.confirm({
      title: `Exécuter sur ${t.label || t.id} (${t.kind})`,
      body: `Opération destructive — vérifiez la requête avant de confirmer.\n\n${preview}`,
      level: "danger",
      entityName: t.id,
      confirmLabel: "Exécuter",
    });
    if (!ok) return;

    setRunning(true);
    setSqlError("");
    setQueryResult(null);
    setExecResult(null);
    setLastMode("exec");
    panel.setActionStatus({ text: "Exécution DML/DDL…", level: "warn" });

    try {
      const r = await panel.tools.call<ExecAsyncResult | QueryResult>(
        "sql_exec",
        { targetId: t.id, query: q, timeoutMs: SQL_TOOL_TIMEOUT_MS },
        { userConfirmedAt: new Date().toISOString(), timeoutMs: SQL_TOOL_TIMEOUT_MS },
      );

      // Si le serveur renvoie un job async (cas standard), on attend les events
      if (r && typeof r === "object" && "jobId" in r && (r as ExecAsyncResult).jobId) {
        setActiveJobId((r as ExecAsyncResult).jobId);
        pushHistory({ query: q, targetId: t.id, mode: "exec", ts: Date.now() });
        // suivi via createEffect (cf. checkExecJob plus bas)
      } else {
        // Fallback : résultat synchrone (au cas où)
        const synced = r as QueryResult;
        setExecResult({
          rowCount: synced.rowCount ?? 0,
          provider: synced.provider,
          durationMs: synced.durationMs,
          columns: synced.columns,
          rows: synced.rows,
          truncated: synced.truncated,
          stdoutBytes: synced.stdoutBytes,
          stderrBytes: synced.stderrBytes,
          stdoutTruncated: synced.stdoutTruncated,
          stderrTruncated: synced.stderrTruncated,
          timedOut: synced.timedOut,
          queuedMs: synced.queuedMs,
          maxConcurrent: synced.maxConcurrent,
          fallback: synced.fallback,
          fallbackReason: synced.fallbackReason,
        });
        pushHistory({ query: q, targetId: t.id, mode: "exec", ts: Date.now() });
        panel.setActionStatus({
          text: `${synced.rowCount ?? 0} ligne(s) affectée(s)${
            synced.durationMs != null ? ` — ${synced.durationMs} ms` : ""
          }`,
          level: "info",
        });
        setRunning(false);
      }
    } catch (e: unknown) {
      setSqlError(e instanceof Error ? e.message : String(e));
      panel.setActionStatus({ text: "Erreur SQL", level: "danger" });
      setRunning(false);
    }
  }

  // Suivi job async pour sql_exec
  let prevJobDone = false;
  function checkExecJob(): void {
    const js = jobState();
    if (!js) return;
    if (js.status === "done" && !prevJobDone) {
      prevJobDone = true;
      const result = (js.result ?? {}) as Partial<QueryResult>;
      setExecResult({
        rowCount: result.rowCount ?? 0,
        provider: result.provider,
        durationMs: result.durationMs,
        columns: result.columns,
        rows: result.rows as SqlRow[] | undefined,
        truncated: result.truncated,
        stdoutBytes: result.stdoutBytes,
        stderrBytes: result.stderrBytes,
        stdoutTruncated: result.stdoutTruncated,
        stderrTruncated: result.stderrTruncated,
        timedOut: result.timedOut,
        queuedMs: result.queuedMs,
        maxConcurrent: result.maxConcurrent,
        fallback: result.fallback,
        fallbackReason: result.fallbackReason,
      });
      panel.setActionStatus({
        text: `${result.rowCount ?? 0} ligne(s) affectée(s)${
          result.durationMs != null ? ` — ${result.durationMs} ms` : ""
        }`,
        level: "info",
      });
      setRunning(false);
      setActiveJobId(null);
      setTimeout(() => {
        prevJobDone = false;
      }, 300);
    } else if (js.status === "error" && !prevJobDone) {
      prevJobDone = true;
      setSqlError(js.error || "Erreur inconnue");
      panel.setActionStatus({ text: "Erreur SQL", level: "danger" });
      setRunning(false);
      setActiveJobId(null);
      setTimeout(() => {
        prevJobDone = false;
      }, 300);
    }
  }

  // ── Colonnes dynamiques pour DataTable ───────────────────────────────────────

  const resultColumns = createMemo<Column<SqlRow>[]>(() => {
    const mode = lastMode();
    let cols: string[] | undefined;
    if (mode === "query") cols = queryResult()?.columns;
    else if (mode === "exec") cols = execResult()?.columns;
    if (!cols || cols.length === 0) return [];
    return cols.map<Column<SqlRow>>((c) => ({
      id: c,
      header: c,
      accessorFn: (row: SqlRow): string => {
        const v = row[c];
        return v == null ? "" : String(v);
      },
      cell: (info) => {
        const v = info.getValue() as string;
        return <>{v === "" ? <span style={{ color: "var(--muted, #888)" }}>NULL</span> : v}</>;
      },
    }));
  });

  const resultRows = createMemo<SqlRow[]>(() => {
    const mode = lastMode();
    if (mode === "query") return queryResult()?.rows ?? [];
    if (mode === "exec") return execResult()?.rows ?? [];
    return [];
  });

  // ── Toolbar ──────────────────────────────────────────────────────────────────

  const actions: JSX.Element = (
    <>
      <select
        class="panel-select"
        value={targetId()}
        onChange={(e) => setTargetId(e.currentTarget.value)}
        disabled={targetsLoading() || targets().length === 0}
        style={{ "min-width": "200px" }}
      >
        <Show when={targets().length === 0}>
          <option value="">{targetsLoading() ? "Chargement…" : "Aucune cible"}</option>
        </Show>
        <For each={targets()}>
          {(t) => (
            <option value={t.id}>
              {t.label || t.id} [{t.kind}/{t.driver ?? "auto"}]{t.allowDestructive ? "" : " (RO)"}
            </option>
          )}
        </For>
      </select>
      <button
        class="btn btn--xs btn--ghost"
        onClick={() => void refreshTargets()}
        disabled={targetsLoading()}
        title="Rafraîchir la liste des cibles"
      >
        ↺ Rafraîchir targets
      </button>
    </>
  );

  return (
    <PanelChrome icon="🗄" title="SQL Server" tagType="core" actions={actions}>
      <PanelLayout title="Requêtes SQL" status={panel.status()}>
        {/* Track job events */}
        <Show when={jobState()}>{checkExecJob() as unknown as JSX.Element}</Show>

        <Show when={targetsErr()}>
          <div class="panel-error" role="alert">
            Erreur chargement targets : {targetsErr()}
          </div>
        </Show>

        <Show when={!panel.enabled()}>
          <div class="panel-data-empty">Module natif requis pour interroger SQL Server.</div>
        </Show>

        <Show when={panel.enabled()}>
          <div class="sql-panel-layout" style={{ display: "flex", "flex-direction": "column", gap: "8px" }}>
            <Show when={currentTarget()}>
              {(t) => (
                <div
                  style={{
                    "font-size": "11px",
                    color: "var(--muted, #888)",
                    display: "flex",
                    gap: "12px",
                    "flex-wrap": "wrap",
                  }}
                >
                  <span>
                    <strong>Host :</strong> {t().host}:{t().port}
                  </span>
                  <span>
                    <strong>DB :</strong> {t().database}
                  </span>
                  <span>
                    <strong>User :</strong> {t().user}
                  </span>
                  <span>
                    <strong>Driver :</strong> {t().driver ?? "auto"}
                    <Show when={t().supportsNative === false}> (CLI)</Show>
                  </span>
                  <span>
                    <strong>Destructif :</strong>{" "}
                    <PanelBadge intent={t().allowDestructive ? "warn" : "muted"}>
                      {t().allowDestructive ? "autorisé" : "refusé"}
                    </PanelBadge>
                  </span>
                </div>
              )}
            </Show>

            <div class="sql-editor-wrap">
              {/* Calque coloré synchronisé — affiché derrière le textarea.
                  innerHTML reçoit uniquement du HTML déjà échappé (highlightToHtml). */}
              <pre
                class="sql-editor-highlight"
                aria-hidden="true"
                ref={(el) => (highlightLayer = el)}
                // eslint-disable-next-line solid/no-innerhtml -- highlightToHtml échappe tout le contenu
                innerHTML={highlightedQuery()}
              />
              <textarea
                class="sql-editor"
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                onScroll={(e) => {
                  if (highlightLayer) {
                    highlightLayer.scrollTop = e.currentTarget.scrollTop;
                    highlightLayer.scrollLeft = e.currentTarget.scrollLeft;
                  }
                }}
                placeholder="-- SELECT TOP 100 * FROM sys.databases"
                spellcheck={false}
                autocomplete="off"
                rows={8}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
                    e.preventDefault();
                    void runQuery();
                  }
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && e.shiftKey) {
                    e.preventDefault();
                    void runExec();
                  }
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "8px", "align-items": "center", "flex-wrap": "wrap" }}>
              <button
                class="btn btn--sm btn--primary"
                onClick={() => void runQuery()}
                disabled={running() || !targetId() || !query().trim()}
                title="Ctrl/Cmd+Enter"
              >
                ▶ Query (SELECT)
              </button>
              <button
                class="btn btn--sm btn--danger"
                onClick={() => void runExec()}
                disabled={running() || !targetId() || !query().trim() || !currentTarget()?.allowDestructive}
                title="Ctrl/Cmd+Shift+Enter — destructif"
              >
                ⚡ Exec (DML/DDL — destructif)
              </button>
              <Show when={running()}>
                <span style={{ "font-size": "12px", color: "var(--muted, #888)" }}>
                  Exécution en cours…
                  <Show when={jobState()?.status}> [{jobState()!.status}]</Show>
                </span>
              </Show>
            </div>

            <Show when={sqlError()}>
              <div
                class="panel-error"
                role="alert"
                style={{
                  "background-color": "rgba(220, 50, 50, 0.12)",
                  border: "1px solid rgba(220, 50, 50, 0.4)",
                  padding: "8px 12px",
                  "border-radius": "4px",
                  color: "#ff8080",
                  "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
                  "font-size": "12px",
                  "white-space": "pre-wrap",
                }}
              >
                <strong>Erreur SQL :</strong> {sqlError()}
              </div>
            </Show>

            {/* Job logs (exec async) */}
            <Show when={lastMode() === "exec" && jobState() && jobState()!.logs.length > 0}>
              <div class="panel-detail-section">
                <h4 style={{ "font-size": "12px", margin: "4px 0" }}>
                  Journal{" "}
                  <PanelBadge
                    intent={jobState()!.status === "done" ? "ok" : jobState()!.status === "error" ? "danger" : "info"}
                  >
                    {jobState()!.status}
                  </PanelBadge>
                </h4>
                <pre class="panel-job-log" style={{ "max-height": "120px", overflow: "auto" }}>
                  <For each={jobState()!.logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
                </pre>
              </div>
            </Show>

            {/* Résultat */}
            <Show when={lastMode() === "query" && queryResult()}>
              {(qr) => (
                <div>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      "font-size": "12px",
                      color: "var(--muted, #888)",
                      margin: "4px 0",
                    }}
                  >
                    <span>
                      <strong>{qr().rowCount}</strong> ligne(s)
                    </span>
                    <Show when={qr().durationMs != null}>
                      <span>{qr().durationMs} ms</span>
                    </Show>
                    <Show when={qr().provider}>
                      <span>driver {qr().provider}</span>
                    </Show>
                    <Show when={qr().fallback}>
                      <PanelBadge intent="warn" title={qr().fallbackReason ?? ""}>
                        fallback CLI
                      </PanelBadge>
                    </Show>
                    <Show when={qr().stdoutBytes != null}>
                      <span>stdout {formatBytes(qr().stdoutBytes!)}</span>
                    </Show>
                    <Show when={qr().queuedMs != null && qr().queuedMs! > 0}>
                      <span>queue {qr().queuedMs} ms</span>
                    </Show>
                    <Show when={qr().stderrBytes != null && qr().stderrBytes! > 0}>
                      <span>stderr {formatBytes(qr().stderrBytes!)}</span>
                    </Show>
                    <Show when={qr().truncated}>
                      <PanelBadge intent="warn">tronqué</PanelBadge>
                    </Show>
                    <Show when={qr().timedOut}>
                      <PanelBadge intent="danger">timeout</PanelBadge>
                    </Show>
                  </div>
                  <Show
                    when={resultRows().length > 0}
                    fallback={<div class="panel-data-empty">Aucune ligne retournée.</div>}
                  >
                    <DataTable<SqlRow> rows={resultRows()} columns={resultColumns()} emptyMessage="Aucune ligne." />
                  </Show>
                </div>
              )}
            </Show>

            <Show when={lastMode() === "exec" && execResult()}>
              {(er) => (
                <div
                  style={{
                    "background-color": "var(--surface-1, #1a1a1a)",
                    border: "1px solid var(--border, #333)",
                    padding: "12px",
                    "border-radius": "4px",
                  }}
                >
                  <div style={{ "font-size": "14px" }}>
                    <strong>{er().rowCount}</strong> ligne(s) affectée(s)
                    <Show when={er().durationMs != null}>
                      <span style={{ color: "var(--muted, #888)", "margin-left": "12px", "font-size": "12px" }}>
                        — {er().durationMs} ms
                      </span>
                    </Show>
                    <Show when={er().provider}>
                      <span style={{ color: "var(--muted, #888)", "margin-left": "12px", "font-size": "12px" }}>
                        driver {er().provider}
                      </span>
                    </Show>
                    <Show when={er().fallback}>
                      <span style={{ "margin-left": "12px" }}>
                        <PanelBadge intent="warn" title={er().fallbackReason ?? ""}>
                          fallback CLI
                        </PanelBadge>
                      </span>
                    </Show>
                    <Show when={er().stdoutBytes != null}>
                      <span style={{ color: "var(--muted, #888)", "margin-left": "12px", "font-size": "12px" }}>
                        stdout {formatBytes(er().stdoutBytes!)}
                      </span>
                    </Show>
                    <Show when={er().queuedMs != null && er().queuedMs! > 0}>
                      <span style={{ color: "var(--muted, #888)", "margin-left": "12px", "font-size": "12px" }}>
                        queue {er().queuedMs} ms
                      </span>
                    </Show>
                    <Show when={er().stderrBytes != null && er().stderrBytes! > 0}>
                      <span style={{ color: "var(--muted, #888)", "margin-left": "12px", "font-size": "12px" }}>
                        stderr {formatBytes(er().stderrBytes!)}
                      </span>
                    </Show>
                    <Show when={er().truncated}>
                      <span style={{ "margin-left": "12px" }}>
                        <PanelBadge intent="warn">tronqué</PanelBadge>
                      </span>
                    </Show>
                    <Show when={er().timedOut}>
                      <span style={{ "margin-left": "12px" }}>
                        <PanelBadge intent="danger">timeout</PanelBadge>
                      </span>
                    </Show>
                  </div>
                  <Show when={er().columns && er().rows && er().rows!.length > 0}>
                    <div style={{ "margin-top": "8px" }}>
                      <DataTable<SqlRow> rows={er().rows!} columns={resultColumns()} emptyMessage="Aucune ligne." />
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            {/* Historique */}
            <Show when={history().length > 0}>
              <div class="panel-detail-section" style={{ "margin-top": "8px" }}>
                <h4
                  style={{ "font-size": "12px", margin: "4px 0", display: "flex", "align-items": "center", gap: "8px" }}
                >
                  Historique ({history().length})
                  <button class="btn btn--xs btn--ghost" onClick={clearHistory} title="Vider l'historique">
                    ✕ Vider
                  </button>
                </h4>
                <div style={{ display: "flex", "flex-direction": "column", gap: "2px" }}>
                  <For each={history()}>
                    {(entry) => (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          "align-items": "center",
                          padding: "4px 6px",
                          "border-radius": "3px",
                          cursor: "pointer",
                          "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
                          "font-size": "11px",
                          "background-color": "var(--surface-1, #1a1a1a)",
                          border: "1px solid var(--border, #2a2a2a)",
                        }}
                        onClick={() => loadFromHistory(entry)}
                        title="Cliquer pour recharger"
                      >
                        <span style={{ "min-width": "44px", "text-align": "center" }}>
                          <PanelBadge intent={entry.mode === "exec" ? "warn" : "info"}>
                            {entry.mode === "exec" ? "EXEC" : "QUERY"}
                          </PanelBadge>
                        </span>
                        <span style={{ color: "var(--muted, #888)", "min-width": "80px" }}>{entry.targetId}</span>
                        <span
                          class="sql-code-inline"
                          style={{
                            flex: "1",
                            overflow: "hidden",
                            "text-overflow": "ellipsis",
                            "white-space": "nowrap",
                          }}
                          // eslint-disable-next-line solid/no-innerhtml -- highlightToHtml échappe tout le contenu
                          innerHTML={highlightToHtml(entry.query, "sql")}
                        />
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>
        </Show>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("sql", SqlPanel);

export default SqlPanel;
